import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Tooltip, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { setAgentChatOpen } from "@plannel/redux/modules/AgentChatState";
import { useAgentChatEngineApi, useAgentChatStatus } from "@plannel/components/agent/AgentChatEngineContext";
import { useHomeSignals } from "@plannel/pages/home/HomeSignalContext";
import { rankSignals, questionTextOf } from "@plannel/pages/home/homeStarterQuestions";
import HomeBreakdown, { hasBreakdown, LEAD_TITLE_MIN_H } from "@plannel/pages/home/HomeBreakdown";
import { FS } from "@plannel/utils/font-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";

/*
 * HomeBroadsheet — 홈의 두 번째 레이아웃. 벤토 그리드(Home.js 의 4열 격자)를 대체하지 않고
 * 나란히 존재한다. 사용자가 상단 토글로 고르고, 고른 값은 벤토와 같은 z_p13n layout 에 들어간다.
 *
 * ── 지면 규칙 (신문) ────────────────────────────────────────────────
 * 카드를 전부 같은 크기로 늘어놓지 않고 **중요도에 따라 지면을 다르게 준다.**
 *   리드   = 지금 가장 심각한 카드 1장   (반폭, 큰 제목 — 오른쪽엔 분해 또는 세컨드)
 *   세컨드 = 그 다음 1장                (한 단, 중간 제목)
 *   브리프 = 나머지 신고 카드           (한 단씩)
 *   지표   = 신고가 없는 카드            (제목 없이 값만, 아래로)
 *   데스크 = 모듈과 무관한 공용 카드     (오른쪽 고정 레일)
 *
 * ⚠️ 중요도는 **여기서 새로 판단하지 않는다.** homeStarterQuestions.rankSignals 가 세운 순서를
 *   그대로 쓴다 — 그 함수는 AI 어시스턴트 카드의 질문 목록도 만든다. 두 곳이 각자 정렬하면
 *   "AI 가 1순위로 물어보는 것"과 "지면의 리드 기사"가 어긋난다.
 *   신호를 안 낸 카드는 헤드라인을 못 받는다(= 정상이라 지면을 차지할 이유가 없다).
 *
 * ⚠️⚠️ **카드는 DOM 위치를 절대 바꾸지 않는다.** 자리는 CSS order / gridColumn 으로만 바꾼다.
 *   카드를 다른 부모(기사 단 ↔ 지표 띠)로 옮기면 React 가 언마운트→재마운트하고,
 *   재마운트된 카드는 데이터가 비어 신호를 못 낸다 → 다시 지표 띠로 이동 → 재조회 →
 *   신호 복귀 → 다시 기사 단으로... **끝나지 않는 진동**이 된다(실제로 전 화면이 깜빡였다).
 *   그래서 기사든 지표든 전부 같은 그리드의 자식으로 **카탈로그 순서 그대로** 렌더하고,
 *   Story 는 신호가 없으면 제목 없이 카드만 그린다(조건부 자식이라 자리는 그대로다).
 *
 * ── 디자인 원본과 다른 점 (Claude Design "Sparse Home Page") ─────────
 *   · 색: 원본은 리터럴 hex(#16191C·#FDFDFC…). 여기서는 **MUI 시맨틱 토큰**을 쓴다 —
 *     앱 규약이 새 hex 금지이고, 리터럴로 박으면 다크 테마에서 글자가 사라진다.
 *   · 헤드라인: 원본은 영어 산문("53 combinations carry inventory risk…"). 그런 문장을 만들
 *     소스가 코드에 없다 → **카드 라벨 + 건수** 조합으로 짓는다(새 번역 키 0개).
 *     이미 6개 언어에 있는 질문 문장(questionTextOf)은 리드 아래 한 줄로 붙인다.
 *     챗이 답할 수 없는 신호는 그 한 줄이 비고 제목과 값만 선다 — 답 없는 질문을 던지지 않는다.
 *   · 서체: 원본은 Source Serif 4 / Space Mono 웹폰트. **앱 기본 스택 하나만 쓴다** —
 *     이 파일에 fontFamily 를 적지 않는다(아래 주석).
 */

/*
 * ⚠️ **이 파일에 fontFamily 를 새로 적지 않는다.** 서체는 font-util.js 가 단일 소스다.
 *
 * 한동안 헤드라인만 시스템 serif(Georgia/Times/나눔명조)로 세워 "신문" 은유를 만들었는데,
 * 같은 홈 화면을 격자↔신문으로 오갈 때 **같은 카드의 글자가 서체째 바뀌어** 다른 화면처럼 읽혔다.
 * 배치를 바꾸는 토글이 서체까지 바꿀 일은 아니라 걷어냈다(사용자 요청).
 *
 * 게다가 그 스택은 한·중·일에서 사실상 제각각이었다 — Georgia·Times 에 CJK 자형이 없어
 * 한국어는 나눔명조로, 일·중은 OS 기본 명조로 떨어져 언어마다 다른 글꼴이 섰다.
 *
 * 제목의 위계는 이제 **굵기(700)와 크기(HEAD_FS)** 가 전부 맡는다 — 둘 다 서체와 무관하게 선다.
 */

// 심각도 → MUI 시맨틱 색. 원본의 #B45309(앰버) 자리는 warning 이 받는다.
const SEV_COLOR = { error: "error.main", warn: "warning.main", info: "primary.main" };

/*
  데스크 레일(공용) — 모듈과 무관해 항상 오른쪽에 선다. 인사말은 마스트헤드가 대신하므로 뺀다.
  ⚠️ aiPrompt 는 레일에 넣지 않는다 — 대화 카드가 좌우 2단(최근 대화 | 시작 질문)이라
    280px 레일에서 두 단이 다 눌려 글자가 한 줄에 두 자씩 끊긴다(실제로 그렇게 나왔다).
    기사 아래 전폭 한 줄로 따로 세운다.
*/
const DESK_KEYS = ["myTasks", "pinned", "meetingAgenda", "notifications", "pipeline"];
const AI_KEY = "aiPrompt";

// 마스트헤드 제호 아래 붙는 메타 줄에서 쓰는 구분자.
const SEP = " · ";

/*
  지면 = 기사 | AI | 데스크 **세 구역**. 구역 사이에는 괘선을 둔다.

  AI 를 기사 그리드의 한 칸으로 넣지 않는 이유: 그러면 리드 카드가 절반으로 눌린다.
  AI 는 뉴스가 아니라 도구라 기사와 같은 격자를 나눠 쓸 이유가 없고, 자기 구역을 가져야
  기사 쪽 카드가 제 폭을 지킨다. (지면 맨 아래로 내리는 것도 아니다 — 스크롤해야 보이면
  "무엇부터 볼까"를 묻는 카드가 제일 안 보이는 자리에 있게 된다.)

  ⚠️ 세 구역을 나란히 세우는 건 xl 부터다. lg(1200) 에서 440+360 을 빼면 기사 영역이
    280px 밖에 안 남아 기사가 아니라 목록이 된다. 그 아래에서는 세로로 쌓는다.

  AI 칸 폭을 늘려 질문 잘림을 고치려는 시도는 실패한다 — 카드가 **내부에서** 폭을 반씩 나눠
    쓰기 때문이다(실측: 칸 520px 인데 질문 문단은 210px). 칸을 넓히는 대신
    HomeAiCard 에 stack 을 켜서 두 목록을 위아래로 쌓았다(aiStack). 그래서 440 이면 충분하다.
*/
/*
  ⚠️ 트랙 수는 **AI 카드가 실제로 있느냐**를 따른다. 3단으로 고정해 두면 AI 를 끈 테넌트에서
    자식이 둘뿐인 3단 그리드가 되어 맨 오른쪽 360px 이 아무도 안 쓰는 채로 남는다.
    게다가 기사 구역은 **넓어지지도 않는다** — 1fr 이 받는 폭은 "전체 − 440 − 360 − 간격" 이라
    그 트랙에 무엇이 들었는지와 무관하게 계산되기 때문이다. 즉 AI 를 끄면 내용은 잃고
    공간은 못 얻고, 데스크만 AI 용 폭(416px)에 들어가 뚱뚱해진다.
*/
/*
  ⚠️ 2026-09-30 변경: AI 는 더 이상 자기 열을 갖지 않는다 — **데스크 레일 맨 위**에 선다.
    열로 두면 기사 구역이 "전체 − 440 − 360" 만 받아 리드·브리프가 눈에 띄게 좁아졌다(사용자 지적).
    위 주석의 "레일에서 두 단이 눌린다" 문제는 AI 카드의 stack(위아래 쌓기) 모드로 해결됐고,
    기사 질문 줄이 이미 AI 에게 물을 입구가 되어 AI 카드가 넓을 이유도 줄었다.
*/
const SHEET_REGIONS = { xs: "1fr", xl: "minmax(0,1fr) 360px" };

/*
  기사 그리드 — 2열. **리드 줄은 항상 두 칸이 다 찬다**: 왼쪽 리드 카드, 오른쪽은 분해(있으면)
  아니면 세컨드 기사. 리드를 전폭으로 늘리는 선택지는 두지 않는다 — 카드 내용이 왼쪽 1/3만
  채워서 오른쪽이 통째로 비어 보인다(두 번 지적받아 고친 자리다).
*/
/*
  6열로 잘게 쪼개 **기사와 지표에 다른 폭**을 준다.
    리드 줄(리드 + 분해/세컨드) 3칸 = 절반 → 한 줄에 2장
    지표(신고 없는 카드)        2칸 = 1/3  → 한 줄에 3장
  지표는 헤드라인도 질문도 없이 값만 있는 카드라 기사만큼 자리를 줄 이유가 없다.
*/
const SHEET_COLS = { xs: "repeat(1, minmax(0,1fr))", sm: "repeat(6, minmax(0,1fr))" };
// 라벨·안내문처럼 줄을 통째로 쓰는 것.
const SPAN_FULL = "1 / -1";
/*
  리드 줄 한 칸. **여기는 늘 절반이다** — 리드와 그 옆(분해 또는 세컨드)이 줄을 꽉 채워야 한다.
  브리프처럼 좁히면 리드 줄 오른쪽에 두 칸이 남아 머리기사 옆이 비어 보인다.
*/
const SPAN_LEAD = { xs: "span 1", sm: "span 3" };
/*
  브리프 한 편 — 지면이 넓어지면 3단이 된다.
  AI 구역이 없으면 기사 구역이 460px 남짓 넓어지는데, 그 폭을 카드 두 장이 나눠 가지면
  값 한 줄짜리 카드가 800px 를 차지해 오히려 더 비어 보인다. 칸을 하나 더 만들어
  **기사 폭을 대략 그대로 두고 편수를 늘리는** 쪽을 택한다(있는 기사를 위로 끌어올리는 것이지
  없는 카드를 지어내는 게 아니다).
  ⚠️ hasAi 를 폭의 대리 지표로 쓴다. 실제 조건은 "지면이 넓은가" 지만 JS 로 폭을 재지 않고,
    그 폭을 460px 씩 바꾸는 건 AI 구역 유무뿐이다.
*/
//   ⚠️ AI 가 레일로 옮겨간 뒤로는 기사 구역 폭이 AI 유무와 무관하다 → 항상 넓은 쪽(3단)이다.
const SPAN_BRIEF = { xs: "span 1", sm: "span 2" };
// 지표 한 장.
const SPAN_QUIET = { xs: "span 1", sm: "span 2" };

/*
  마스트헤드 — 제호(회사명) + 메타 한 줄.
  ⚠️ 이 컴포넌트만 useHomeSignals() 를 부른다. 지면 본체에서 부르면 신호 하나에
    카드 전체가 다시 그려진다(HomeSignalContext 주석의 "소비자는 말단" 규칙).
*/
/*
  회사명을 못 받았을 때의 제호. 신문에 제호가 없을 수는 없어서 제품명으로 떨어진다.
  ⚠️ 카드 라벨(인사말 등) 을 폴백으로 쓰면 안 된다 — 제호 자리에 "Greeting" 이 찍힌다.
*/
const MASTHEAD_FALLBACK = "PlanNEL";

/*
  ⚠️ meta 에 권한 모듈 코드(BF DP IP RP MP)를 넣지 않는다. 바로 아래 섹션 탭이 같은 목록을
    전체 이름으로, 그 아래 버전 줄이 같은 목록을 버전·상태까지 붙여 보여 준다 — 세 번째 반복은
    읽을 이유가 없는 글자다.
*/
const Masthead = ({ tt, company, today }) => {
  const signals = useHomeSignals();
  const alerts = signals.filter((s) => s.severity === "error" || s.severity === "warn").length;

  const meta = [
    today,
    alerts > 0 ? tt("homeNeedsCheckCount", { count: alerts }) : null,
  ].filter(Boolean).join(SEP);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, flexWrap: "wrap", pb: 1.25, borderBottom: "2px solid", borderColor: "text.primary" }}>
      <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1, letterSpacing: "-0.01em", color: "text.primary" }}>
        {company || MASTHEAD_FALLBACK}
      </Typography>
      <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
        {meta}
      </Typography>
    </Box>
  );
};

// 기사 제목 — "카드 라벨 + 건수". 산문을 만들지 않으므로 새 번역 키가 필요 없다.
//   count 가 없는 신호(bfRunStatus 등)는 라벨만 선다.
const headlineOf = (tt, label, signal) => (typeof signal?.count === "number"
  ? `${label} ${signal.count.toLocaleString()}${tt("homeCountUnit")}`
  : label);

const HEAD_FS = { lead: 26, second: 17, brief: 14.5 };

/*
  기사 질문 한 줄 → Agent Chat 으로 보내 답을 받는다.
  이 문장은 애초에 **챗이 답할 수 있는 것만** 나온다(questionTextOf 가 askable:false 를 막는다) —
  AI 어시스턴트 카드의 '업무 시작 질문' 과 같은 문장이다. 그래서 여기서도 같은 방식으로 보낸다.

    · 새 세션으로 보낸다 — 기사 질문끼리는 서로 무관한 조회라 한 세션에 쌓이면 LLM 컨텍스트가
      섞인다(HomeAiCard 의 '목록으로 → 다음 질문은 새 세션' 과 같은 규칙).
      ⚠️ 진행 중인 턴이 있으면 resetChat 하지 않는다 — 서버 턴이 고아가 된다(HomeAiCard.doSend 주석).
        그 경우 send 가 막히고 패널이 "답변 중" 안내를 띄운다.
    · 답은 우측 패널에서 본다 — 기사 옆에는 답을 그릴 자리가 없고, AI 카드는 꺼져 있을 수 있다.
      viewId 가 "panel" 인 이유: 막혔을 때 입력 포커스가 패널로 가야 한다.

  ⚠️ 챗 상태를 구독하는 건 이 말단뿐이다 — Story 나 지면 본체에서 구독하면 턴마다 지면 전체가 다시 그려진다.
*/
const AskDeck = ({ tt, text }) => {
  const dispatch = useDispatch();
  const api = useAgentChatEngineApi("panel");
  const status = useAgentChatStatus();

  const ask = () => {
    dispatch(setAgentChatOpen(true));
    if (status.hasConversation && !status.chatBusy && !status.chatAborting && !status.loadingSession) {
      api.resetChat();
    }
    api.send(text, { trusted: true });
  };

  return (
    <Tooltip title={tt("homeAskAi")} placement="bottom-start" disableInteractive>
      <Typography component="button" type="button" onClick={ask}
        sx={{
          appearance: "none", background: "none", border: 0, p: 0, font: "inherit", textAlign: "left",
          cursor: "pointer", display: "block", width: "fit-content", maxWidth: "100%",
          fontSize: FS.SUB, color: "text.secondary", mt: 0.75, lineHeight: 1.6,
          "&:hover, &:focus-visible": { color: "primary.main", textDecoration: "underline" },
        }}>
        <AutoAwesomeIcon sx={{ fontSize: "1.05em", verticalAlign: "-0.15em", mr: 0.5, color: "primary.main" }} />
        {text}
      </Typography>
    </Tooltip>
  );
};

/*
  기사 한 편 = 제목 + 심각도 눈금 + 질문 한 줄 + 카드 본체(실제 값).

  ⚠️ 신호가 없으면(= 지표 띠로 내려간 카드) 제목 3줄이 **null 로 빠질 뿐 구조는 그대로**다.
    조건부 자식은 자리를 지키므로 children(카드)의 위치 인덱스가 안 변하고, 따라서 카드는
    기사↔지표를 오가도 리마운트되지 않는다. 이 파일 상단의 진동 주석 참고.
*/
// showQuestion — AI 어시스턴트 카드가 보일 때만 질문 줄을 세운다(지면 본체의 showQuestions 주석).
const Story = ({ tt, label, signal, size, eyebrow, eyebrowColor, quiet = false, showQuestion = false, children }) => {
  const deck = showQuestion ? questionTextOf(signal, tt) : null;

  return (
    // flex:1 + width:100% — 그리드 칸(display:flex)의 자식이라 이게 없으면 내용 폭으로 쪼그라든다.
    <Box component="article" sx={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1, width: "100%" }}>
      {/*
        제목 띠. 리드일 때만 높이를 고정해 옆 칸(분해 목록)의 제목과 같은 선에 세운다.
        justifyContent:flex-end — 띠가 남으면 글자를 아래로 붙여, 리드문과 분해 제목이
        같은 높이에서 끝나고 그 아래로 내용이 나란히 시작한다.
        ⚠️ 이 Box 는 **항상** 그린다(조건부로 만들면 children 의 위치 인덱스가 흔들린다).
      */}
      <Box sx={{
        display: "flex", flexDirection: "column", justifyContent: "flex-end",
        // 리드 줄에 서는 둘(리드·세컨드)은 같은 띠 높이를 써야 카드가 같은 선에서 시작한다.
        ...(size === "lead" || size === "second" ? { minHeight: LEAD_TITLE_MIN_H } : {}),
      }}>
        {/*
          눈썹줄(kicker) = **그 기사가 속한 모듈명**, 색도 **모듈색**(ColorUtil.colorSet).
          ⚠️ 여기에 cardKey 같은 내부 식별자나 "LEAD"/"BRIEF" 같은 하드코딩 영어를 넣지 말 것 —
            사용자가 읽을 값이 아니고 언어팩에도 없다. 자리(리드/세컨드/브리프)는 이미
            제목 크기와 단 폭이 말하고 있다.
          색을 심각도가 아니라 모듈로 칠하는 이유: 앱 규칙이 "색이 곧 권한 소속"이라
          (Home.js CARD_ICON 주석) 모듈명에 심각도 색을 칠하면, 바로 아래 카드 아이콘이
          쓰는 모듈색과 **같은 모듈을 두 가지 뜻의 색으로** 칠하게 된다.
          심각도는 막대 색과 리드문이 이미 전달한다.
        */}
        {(signal || quiet) && eyebrow && (
          <Typography sx={{ fontSize: FS.CAPTION, fontWeight: 700, letterSpacing: "0.08em", color: eyebrowColor || "text.secondary" }}>
            {eyebrow}
          </Typography>
        )}
        {/* 조용한 리드(신고 없는 면의 대표 지표)도 제목은 선다 — 신호가 없을 뿐 이름은 있다.
            건수가 없으니 headlineOf 가 라벨만 돌려준다. */}
        {(signal || quiet) && (
          <Typography sx={{
            fontWeight: 700, color: "text.primary", mt: 0.75,
            fontSize: HEAD_FS[size] ?? HEAD_FS.brief,
            lineHeight: 1.2, letterSpacing: "-0.015em", textWrap: "pretty",
          }}>
            {headlineOf(tt, label, signal)}
          </Typography>
        )}
        {deck && <AskDeck tt={tt} text={deck} />}
      </Box>
      {/* 값은 카드가 스스로 그린다 — 여기서 숫자를 다시 읽지 않는다. */}
      <Box sx={{ mt: (signal || quiet) ? 1.25 : 0, display: "flex", minWidth: 0, flex: 1 }}>{children}</Box>
    </Box>
  );
};

/*
  섹션 탭 — 신문의 "면"에 해당한다. **한 번에 한 모듈만 지면에 올린다**(홈의 원래 요구사항).
  전체(ALL)를 고르면 권한 있는 모듈 전부를 한 지면에 싣는다.

  공용(core) 카드는 어느 탭에서도 오른쪽 데스크 레일에 그대로 남는다 — 모듈 소속이 아니라서다.

  탭마다 그 모듈의 **최악 심각도**를 점으로 단다. 탭을 열어 보지 않아도 어디가 급한지 보인다.
*/
const SectionTabs = ({ tt, sections, current, onChange, worstOf }) => (
  <Box role="tablist" sx={{ display: "flex", flexWrap: "wrap", borderBottom: "1px solid", borderColor: "divider" }}>
    {sections.map((s) => {
      const active = s.key === current;
      const worst = worstOf(s.key);
      return (
        <Box
          key={s.key} role="tab" component="button" type="button"
          aria-selected={active}
          onClick={() => onChange(s.key)}
          sx={{
            appearance: "none", background: "none", border: 0, cursor: "pointer", font: "inherit",
            display: "flex", alignItems: "center", gap: 0.75, px: 1.75, py: 1.25,
            fontSize: FS.TITLE, fontWeight: 600,
            color: active ? "text.primary" : "text.secondary",
            borderBottom: "3px solid",
            borderColor: active ? "primary.main" : "transparent",
            "&:hover": { color: "text.primary" },
          }}
        >
          {s.label}
          {s.code && (
            <Typography component="span" sx={{ fontSize: FS.CAPTION, color: "text.disabled", fontWeight: 500 }}>
              {s.code}
            </Typography>
          )}
          {worst && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: SEV_COLOR[worst] ?? "text.disabled" }} />}
        </Box>
      );
    })}
  </Box>
);

// 단 제목(YOUR DESK 등) — 원본의 Space Mono 라벨 자리.
const ColumnLabel = ({ children }) => (
  <Typography sx={{ fontSize: FS.CAPTION, fontWeight: 700, letterSpacing: "0.1em", color: "text.secondary", textTransform: "uppercase", mb: 1 }}>
    {children}
  </Typography>
);

/*
  계획 버전 줄 — 제호와 기사 사이의 "발행 정보" 자리.

  신문에 인사말은 안 어울리지만, 인사말 카드가 들고 있던 **계획 버전 상태**는 지면에서도
  필요하다: 지금 보고 있는 숫자가 어느 버전의 것인지, 그 버전이 확정본인지 작업 중인지.
  칩을 누르면 그 모듈 화면이 해당 버전으로 프리셋되어 열린다(벤토 인사말 카드와 같은 동작).

  ⚠️ 색·라벨은 VersionMgmt 와 **같은 유틸**을 쓴다(CodeUtil.versionStatusLabel /
    ColorUtil.versionStatusColor). 여기서 새로 칠하면 같은 상태가 화면마다 다른 색이 된다.
  ⚠️ 이 줄은 카드 설정의 "인사말" 토글을 따른다 — 끄면 사라진다. 그 항목이 신문 모드에서
    아무 일도 안 하던 것을 여기에 묶었다.
*/
const VersionStrip = ({ tt, versions, loading, onVersionClick, moduleNameOf }) => {
  // 로딩 중에도 자리를 비워 둔다 — 칩이 늦게 들어오며 아래 기사 전체를 밀어내지 않도록.
  if (loading || !versions?.length) return <Box sx={{ minHeight: 22 }} />;
  const chipSx = { display: "inline-block", flexShrink: 0, whiteSpace: "nowrap", fontSize: FS.CAPTION, fontWeight: 600, borderRadius: 5, px: 1, py: 0.25 };
  const noneSx = { color: "text.secondary", bgcolor: "action.hover", border: "1px solid", borderColor: "divider" };
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
      {versions.map((v) => {
        const modLabel = moduleNameOf(v.moduleCd) ?? v.moduleCd;
        if (!v.name) {
          return (
            <Typography key={v.moduleCd} component="span" sx={{ ...chipSx, ...noneSx }}>
              {`${modLabel} · ${tt("homeNoVersion")}`}
            </Typography>
          );
        }
        return (
          <Typography key={v.moduleCd} component="span" onClick={() => onVersionClick?.(v)}
            sx={{ ...chipSx, ...ColorUtil.tintChipSx(ColorUtil.versionStatusColor(v.statusCd)),
                  cursor: "pointer", transition: "box-shadow .12s, filter .12s",
                  "&:hover": { boxShadow: 2, filter: "brightness(0.97)" } }}>
            {`${modLabel} ${v.name} · ${CodeUtil.versionStatusLabel(v.statusCd)}`}
          </Typography>
        );
      })}
    </Box>
  );
};

const ALL = "ALL";

const HomeBroadsheet = ({ ctx, cards, renderCard, cardLabel, moduleLabel, moduleColor, company, section, onSection, onReorder }) => {
  const { tt, today, permitted, versions, onVersionClick } = ctx;
  const signals = useHomeSignals();
  /*
    지표 띠 카드 끌어 옮기기 — **지표만** 대상이다.
    기사(리드·세컨드·브리프) 자리는 rankSignals 가 정한다. 거기서 사용자가 자리를 고르면
    "가장 급한 것이 리드" 라는 약속이 깨진다. 반대로 지표는 신고가 없는 카드들이라
    자리에 의미가 없어 사용자가 정하는 게 맞다.
    ⚠️ 저장은 layout.order 하나다(= 벤토 순서와 같은 값). 카드 설정 모달이 보여 주는
      순서 목록도 이것 하나라, 배치마다 순서를 따로 두면 "왜 여기만 순서가 다르지" 가 된다.
  */
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  /*
    탭 목록 = 전체 + 권한 있는 계획 모듈. AI 는 계획 모듈이 아니라 탭을 주지 않는다
    (카드 자체가 지면 아래 전폭으로 항상 서 있고, 모듈처럼 골라 볼 대상이 아니다).
  */
  const sections = useMemo(() => [
    { key: ALL, label: tt("all") },
    ...permitted.map((m) => ({ key: m.key, label: tt(m.nameKey), code: m.key })),
  ], [permitted, tt]);

  // 저장된 탭이 권한에서 빠졌으면(모듈 회수) 전체로 되돌린다 — 빈 지면을 보여 주지 않는다.
  const current = sections.some((s) => s.key === section) ? section : ALL;

  // 탭 배지 — 그 모듈 카드가 신고한 것 중 최악. 전체 탭은 지면 전부를 통틀어 본다.
  const worstOf = (key) => {
    const inScope = signals.filter((s) => (key === ALL ? true : s.module === key));
    if (inScope.some((s) => s.severity === "error")) return "error";
    if (inScope.some((s) => s.severity === "warn")) return "warn";
    return null;
  };

  /*
    지면 배치 — **DOM 순서가 아니라 CSS order 만 계산한다.**
      slot   : "lead" | "second" | "brief" | null(신호 없음 → 지표)
      order  : 그리드 안에서의 시각적 순서
      full   : 전폭(리드·구분 라벨)인지

    카드 자체는 아래에서 cards(카탈로그 순서) 그대로 렌더되므로 DOM 위치가 절대 안 변한다.
  */
  const place = useMemo(() => {
    const byKey = Object.fromEntries(cards.map((c) => [c.key, c]));
    const railSet = new Set([...DESK_KEYS, AI_KEY]);
    // 선택한 면에 속하는 카드만 지면에 올린다. 공용(core)은 레일에 있으므로 여기서 걸러도 안 사라진다.
    const inSection = (c) => current === ALL || c.module === current;
    const ranked = rankSignals(signals)
      .filter((s) => byKey[s.cardKey] && !railSet.has(s.cardKey) && inSection(byKey[s.cardKey]));

    /*
      **조용한 면의 리드** — 신고가 하나도 없으면 그 면은 기사 없이 지표 띠만 남아,
      제호와 탭 아래가 통째로 비어 보인다(BF 가 정상 실행되고 편차 0 이면 실제로 그렇다).
      그럴 때는 그 모듈의 **대표 지표**를 리드로 올린다.

      대표는 "쪼갤 수 있는 카드 중 첫 번째" 다 — 분해가 붙어야 리드 옆 칸이 답으로 채워지고,
      그게 이 자리의 목적이다(질문만 던지고 답이 없으면 화면을 열어 보러 가야 한다).
      분해가 붙은 카드가 없으면 아무것도 올리지 않는다 — 반쪽짜리 리드를 만드느니 지표로 둔다.

      ⚠️ 전체(ALL) 면에는 적용하지 않는다. 여러 모듈이 섞인 지면에서 아무 모듈의 지표나
        리드로 세우면 "가장 급한 것이 리드" 라는 약속이 깨진다. 전부 조용하면 조용한 게 맞다.
      ⚠️ 신호가 아니므로 심각도·건수·질문 한 줄이 없다. 없는 문장을 지어내지 않는다 —
        제목(카드 이름)과 값, 그리고 옆의 분해가 스스로 말한다.
    */
    const quietLead = (ranked.length === 0 && current !== ALL)
      ? cards.find((c) => inSection(c) && !railSet.has(c.key) && c.key !== "greeting" && hasBreakdown(c.key))
      : null;

    /*
      리드 줄은 항상 두 칸이다: **왼쪽 리드 카드 | 오른쪽 무언가.**
      오른쪽에 세울 것이 분해면 분해를, 분해가 없는 카드면 **세컨드 기사**를 올린다.
      리드를 전폭으로 늘리는 선택지는 두지 않는다 — 카드 내용이 왼쪽 1/3만 채워서
      오른쪽이 통째로 비어 보인다(이미 두 번 지적받아 고친 자리다).
    */
    const showBreakdown = quietLead ? true : (ranked.length > 0 && hasBreakdown(ranked[0].cardKey));
    // 분해가 없으면 세컨드가 리드 옆(order 2)으로 올라가므로, 브리프에 남는 기사가 하나 줄어든다.
    const inLeadRow = showBreakdown ? 1 : Math.min(2, ranked.length);

    const map = {};
    ranked.forEach((s, i) => {
      map[s.cardKey] = {
        signal: s,
        /*
          크기는 **순위가 아니라 자리**로 정한다. 순위 2번을 늘 "second"(큰 제목)로 잡으면,
          분해가 있어 그 카드가 리드 옆이 아니라 브리프 줄로 내려갈 때 같은 줄에
          17px 과 14.5px 이 나란히 서서 제목 띠 높이가 달라지고 카드가 어긋난다(실측 3px).
        */
        slot: i === 0 ? "lead" : (i === 1 && !showBreakdown ? "second" : "brief"),
        // 리드 1, (분해 없을 때) 세컨드 2, 브리프 라벨 9, 기사 10~. 지표 라벨 999, 지표 1000~.
        order: i === 0 ? 1 : (i === 1 && !showBreakdown ? 2 : 10 + i),
      };
    });
    // 조용한 리드 — 신고가 없으니 signal 도 없다. 자리만 리드다(제목 크기·반폭).
    if (quietLead) map[quietLead.key] = { signal: null, slot: "lead", order: 1, quiet: true };
    // 신고 없는 카드 = 지표. 카탈로그 순서대로 뒤에 붙는다.
    cards.forEach((c, i) => {
      if (map[c.key] || railSet.has(c.key) || c.key === "greeting" || !inSection(c)) return;
      map[c.key] = { signal: null, slot: null, order: 1000 + i };
    });
    return {
      map,
      storyCount: ranked.length || (quietLead ? 1 : 0),
      showBreakdown,
      briefCount: Math.max(0, ranked.length - inLeadRow),
      // 신고가 하나도 없다 — 조용한 리드가 서더라도 그 사실은 따로 적어야 한다.
      noSignals: ranked.length === 0,
      // 분해 블록이 쪼갤 대상. 신고가 있으면 그 리드, 없으면 조용한 리드다.
      leadKey: ranked[0]?.cardKey ?? quietLead?.key ?? null,
      leadModule: (ranked[0] && byKey[ranked[0].cardKey]?.module) ?? quietLead?.module ?? null,
    };
  }, [signals, cards, current]);

  const { map, storyCount, showBreakdown, briefCount, leadKey, leadModule, noSignals } = place;
  /*
    데스크 레일 카드 — **순서는 사용자 설정(layout.order)을 따른다.**
    DESK_KEYS 는 "누가 레일에 속하는가" 만 정한다. 예전엔 DESK_KEYS 배열 순서대로 그렸는데,
    그러면 카드 설정에서 순서를 바꿔도 레일은 그대로라 목록과 화면이 어긋난다(실제로 그랬다).
    cards 는 이미 visibleOrder(= 저장된 순서)라 그대로 거르기만 하면 된다.
  */
  const deskCards = cards.filter((c) => DESK_KEYS.includes(c.key));
  /*
    버전 줄 노출 = 인사말 카드가 켜져 있는가. cards 는 hidden 을 이미 걸러낸 목록이라
    카드 설정에서 "인사말" 을 끄면 여기서도 사라진다 — 신문 모드에서 아무 일도 하지 않던
    그 토글에 뜻을 붙인 것이다.
  */
  const showVersions = cards.some((c) => c.key === "greeting");
  const aiCard = cards.find((c) => c.key === AI_KEY) ?? null;
  /*
    기사 질문 줄은 **AI 어시스턴트 카드와 한 몸**이다 — 카드가 보일 때만 질문도 선다.
    질문은 AI 에게 묻는 문장이라, 카드를 끈(또는 ROLE_APP_AI 가 없어 카드가 안 오는) 사용자에게
    남겨 두면 물을 곳 없는 질문이 된다. 권한은 따로 보지 않는다 — cards 는 이미 권한·숨김으로
    걸러진 목록이라 aiCard 가 있으면 권한도 있다(판정이 두 곳으로 갈라지지 않게).
  */
  const showQuestions = Boolean(aiCard);
  // 지면에 실리는 카드 = 데스크·AI·인사말 제외. **카탈로그 순서 고정**(자리는 order 가 정한다).
  const sheetCards = cards.filter((c) => map[c.key]);
  const quietCount = sheetCards.length - storyCount;
  /*
    리드가 신고한 신호 — 분해 블록이 "무엇을 쪼개는지"와 "전체가 몇 건인지"를 여기서 받는다.
    ⚠️ 분해는 **전체 면에서도** 그린다. 한때 모듈 면에서만 그렸는데("리드 하나만 쪼개면 나머지
      모듈이 억울하다"), 근거가 약했다 — 리드는 이미 큰 제목과 넓은 자리를 받는 특별 대우고,
      던지는 질문도 어느 면에서든 같다.
  */
  const leadSignal = sheetCards.map((c) => map[c.key]).find((p) => p.slot === "lead")?.signal ?? null;


  const labelSx = (order) => ({ gridColumn: "1 / -1", order, mt: order > 900 ? 1.5 : 0.5, pt: 1.5, borderTop: "2px solid", borderColor: "text.primary" });

  return (
    /* flex:1 + minWidth:0 — 부모(Home)가 준 세로를 받아 아래 구역 그리드에 넘긴다. */
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
      <Masthead tt={tt} company={company} today={today} />

      <SectionTabs tt={tt} sections={sections} current={current} onChange={onSection} worstOf={worstOf} />

      {/* 발행 정보 — 제호와 기사 사이. 지금 보는 숫자가 어느 계획 버전의 것인지 밝힌다. */}
      {showVersions && (
        <VersionStrip tt={tt} versions={versions} loading={ctx.versionsLoading} onVersionClick={onVersionClick}
          moduleNameOf={(mod) => moduleLabel?.(mod)} />
      )}

      {/*
        ⚠️ 바깥 그리드에는 alignItems:"start" 를 주지 않는다(기본 stretch 유지).
          start 로 두면 각 칸이 제 내용 높이까지만 자라서, 구역 사이 괘선이
          기사가 짧은 면(카드 2~3장짜리 모듈)에서 중간에 뚝 끊긴다. 신문의 단 괘선은
          단이 비어도 끝까지 내려가야 단으로 읽힌다.
          칸 **안쪽** 정렬은 각 칸이 자기 alignItems 로 따로 잡는다(카드가 세로로 늘어나지 않게).
      */}
      <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: SHEET_REGIONS, flex: "1 0 auto" }}>
        {/* ── ① 기사 구역 ─────────────────────── */}
        <Box sx={{
          /*
            alignItems:stretch(기본) — 같은 줄 카드가 **가장 큰 것에 맞춰 같은 높이**가 된다.
              start 로 두면 각 카드가 제 내용 높이만 차지해 아래가 들쭉날쭉해진다.
              고정 minHeight 로 맞추지 않는 이유: 내용이 그보다 길면 다시 어긋나고
              짧으면 빈 공간만 남는다 — 매직 넘버가 하나 더 느는 것뿐이다.
            alignContent:start — 칸이 stretch 로 커진 만큼 **행이 늘어나지 않게**.
              없으면 남는 높이가 행 사이 여백으로 배분돼 기사와 지표가 뚝뚝 떨어져 보인다.
          */
          display: "grid", gap: 2.5, alignContent: "start", minWidth: 0,
          gridTemplateColumns: SHEET_COLS,
        }}>
          {/*
            리드 지표 분해 — 리드가 던진 질문("45건이 어느 거점·품목인가")의 답이다.
            order 2 = 리드 바로 옆(같은 줄). 붙어 있어야 질문과 답으로 읽힌다.
          */}
          {showBreakdown && (
            <Box sx={{ order: 2, gridColumn: SPAN_LEAD, display: "flex", minWidth: 0 }}>
              {/* ⚠️ leadSignal 이 아니라 leadKey 를 쓴다 — 조용한 리드(신고 없는 면)에는
                  신호가 없고, 그래도 쪼갤 카드는 있다. total 도 신고에서만 온다(건수가 없으면
                  "그 외 N건" 을 못 세는데, 그건 비율 분해라 애초에 세지 않는다). */}
              <HomeBreakdown
                tt={tt}
                cardKey={leadKey}
                total={typeof leadSignal?.count === "number" ? leadSignal.count : null}
                title={cardLabel(leadKey)}
                color={moduleColor?.(leadModule)}
              />
            </Box>
          )}
          {/*
            신고가 하나도 없다 = 오늘 급한 게 없다. 빈 단을 늘리지 않고 그렇게 적는다.
            조용한 리드가 섰으면 그 아래(order 9, 브리프 자리)로 내린다 — 리드 위에 두면
            "확인할 것 없음" 이 제호 바로 밑 머리기사처럼 읽힌다.
          */}
          {noSignals && (
            <Box sx={{ gridColumn: SPAN_FULL, order: storyCount > 0 ? 9 : 1, ...(storyCount > 0 ? { mt: 0.5, pt: 1.5, borderTop: "2px solid", borderColor: "text.primary" } : {}) }}>
              <ColumnLabel>{tt("homeBriefs")}</ColumnLabel>
              <Typography sx={{ fontSize: FS.SUB, color: "text.secondary" }}>{tt("homeTasksAllDone")}</Typography>
            </Box>
          )}
          {/* 라벨은 아래에 실제로 기사가 남아 있을 때만 — 세컨드가 리드 옆으로 올라가면
              기사 2편짜리 면에서는 라벨만 남고 그 아래가 빈다. */}
          {briefCount > 0 && (
            <Box sx={labelSx(9)}><ColumnLabel>{tt("homeBriefs")}</ColumnLabel></Box>
          )}
          {quietCount > 0 && (
            <Box sx={labelSx(999)}><ColumnLabel>{tt("homeOtherFigures")}</ColumnLabel></Box>
          )}

          {sheetCards.map((c) => {
            const p = map[c.key];
            const quiet = !p.slot;                    // 신고 없는 카드 = 지표 띠
            const canDrag = quiet && Boolean(onReorder);
            const isOver = canDrag && overKey === c.key && dragKey && dragKey !== c.key;
            return (
              <Box key={c.key}
                draggable={canDrag}
                onDragStart={canDrag ? (e) => { setDragKey(c.key); e.dataTransfer.effectAllowed = "move"; } : undefined}
                onDragEnd={canDrag ? () => { setDragKey(null); setOverKey(null); } : undefined}
                onDragOver={canDrag ? (e) => { e.preventDefault(); if (overKey !== c.key) setOverKey(c.key); } : undefined}
                onDragLeave={canDrag ? () => { if (overKey === c.key) setOverKey(null); } : undefined}
                onDrop={canDrag ? (e) => { e.preventDefault(); onReorder(dragKey, c.key); setDragKey(null); setOverKey(null); } : undefined}
                sx={{
                order: p.order,
                // 기사는 반폭(오른쪽은 분해 또는 세컨드), 지표는 1/3 폭.
                /*
                  리드 줄은 절반(SPAN_LEAD), 브리프는 지면 폭에 따라, 지표는 1/3.
                  브리프만 좁히는 이유는 spanBrief 주석 참고 — 리드 줄까지 좁히면 줄이 안 찬다.
                */
                gridColumn: p.slot === "brief" ? SPAN_BRIEF :(p.slot ? SPAN_LEAD : SPAN_QUIET),
                display: "flex", minWidth: 0, minHeight: p.slot ? undefined : 112,
                // 끌 수 있다는 신호와 드롭 대상 강조 — 벤토 그리드와 같은 관용구.
                ...(canDrag ? { cursor: "grab", "&:active": { cursor: "grabbing" } } : {}),
                opacity: dragKey === c.key ? 0.4 : 1,
                outline: isOver ? "2px dashed" : "none",
                outlineColor: "primary.main",
                outlineOffset: "-2px",
                borderRadius: 1.5,
                transition: "opacity 0.12s",
              }}>
                {/* 눈썹줄은 전체 면에서만 — 모듈 면에서는 모든 기사가 같은 모듈이라 같은 말이 반복된다. */}
                <Story tt={tt} label={cardLabel(c.key)} signal={p.signal} size={p.slot ?? "brief"}
                  quiet={Boolean(p.quiet)}
                  showQuestion={showQuestions}
                  eyebrow={current === ALL ? moduleLabel(c.module) : null}
                  eyebrowColor={moduleColor?.(c.module)}>
                  {renderCard(c)}
                </Story>
              </Box>
            );
          })}
        </Box>

        {/* ── ② 데스크 레일 (공용, 항상 같은 자리) — AI 어시스턴트가 맨 위에 선다 ──
            xl 미만에서는 아래로 내려가므로 세로로만 쌓지 않고 가로로 흐른다
            (전폭에서 4장을 세로로 쌓으면 스크롤만 길어진다). */}
        <Box sx={{
          minWidth: 0, display: "grid", gap: 1.5, alignItems: "start", alignContent: "start",
          gridTemplateColumns: { xs: "repeat(auto-fit, minmax(240px, 1fr))", xl: "1fr" },
          pl: { xl: 3 }, borderLeft: { xl: "1px solid" }, borderColor: { xl: "divider" },
          pt: { xs: 1.5, xl: 0 }, borderTop: { xs: "2px solid", xl: "none" },
        }}>
          {/*
            AI 어시스턴트 — 레일 맨 위, 전폭(가로로 흐를 때도 한 줄을 다 쓴다).
            자기 라벨을 달아 아래 '내 데스크' 카드들과 구역을 나눈다.
            340 = 시작 질문 5건(최대치)이 스크롤 없이 들어가는 높이(stack 이라 세로가 더 필요하다 —
            220 이면 5건 중 2.5건만 보였다, 실측 scrollHeight 186).
          */}
          {aiCard && (
            <Box sx={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", minWidth: 0 }}>
              <ColumnLabel>{tt("homeAiPrompt")}</ColumnLabel>
              <Box sx={{ display: "flex", minWidth: 0, minHeight: 340 }}>{renderCard(aiCard)}</Box>
            </Box>
          )}
          <Box sx={{ gridColumn: "1 / -1", ...(aiCard ? { mt: 1 } : {}) }}><ColumnLabel>{tt("homeDeskRail")}</ColumnLabel></Box>
          {deskCards.map((c) => (
            <Box key={c.key} sx={{ display: "flex", minWidth: 0 }}>{renderCard(c)}</Box>
          ))}
        </Box>
      </Box>

    </Box>
  );
};

export default HomeBroadsheet;
